import React, { useCallback, useEffect, useState } from 'react';
import { pricebookApi, PricebookSyncStatus } from '../services/api/pricebookApi';
import { usePageTitle } from '../hooks/usePageTitle';

const TABLE_LABELS: Record<string, string> = {
  pb_departments: 'Departments',
  pb_price_groups: 'Price Groups',
  pb_skus: 'SKUs',
  pb_sku_upcs: 'SKU UPCs',
  pb_sku_quantity_pricing: 'SKU Quantity Pricing',
  pb_sku_linked_skus: 'Linked SKUs',
  pb_sku_linkable_skus: 'Linkable SKUs',
  pb_price_group_quantity_pricing: 'Price Group Quantity Pricing',
};

const PricebookSyncPage: React.FC = () => {
  usePageTitle('Pricebook Sync');

  const [status, setStatus] = useState<PricebookSyncStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<'sync' | 'resync' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pricebookApi.getStatus();
      setStatus(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch pricebook sync status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSyncNow = async () => {
    setActionLoading('sync');
    setError(null);
    setNotice(null);
    try {
      const result = await pricebookApi.syncNow();
      setNotice(`Sync finished: ${result.outcome}`);
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Sync failed.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceFullResync = async () => {
    if (!window.confirm('This clears the stored revision and re-downloads the entire pricebook from scratch. Continue?')) {
      return;
    }

    setActionLoading('resync');
    setError(null);
    setNotice(null);
    try {
      const result = await pricebookApi.forceFullResync();
      setNotice(`Full re-sync finished: ${result.outcome}`);
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Full re-sync failed.');
    } finally {
      setActionLoading(null);
    }
  };

  const isBusy = actionLoading !== null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Pricebook Sync</h1>
          <div className="flex gap-2">
            <button
              onClick={handleSyncNow}
              disabled={isBusy}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                isBusy ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {actionLoading === 'sync' ? 'Syncing...' : 'Sync Now'}
            </button>
            <button
              onClick={handleForceFullResync}
              disabled={isBusy}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                isBusy ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {actionLoading === 'resync' ? 'Re-syncing...' : 'Force Full Re-sync'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
            <button onClick={() => setError(null)} className="float-right text-red-500 hover:text-red-700">×</button>
          </div>
        )}

        {notice && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
            {notice}
            <button onClick={() => setNotice(null)} className="float-right text-blue-500 hover:text-blue-700">×</button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>

          {loading && !status ? (
            <p className="text-gray-500">Loading...</p>
          ) : status ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="text-gray-500">Last Revision</dt>
                <dd className="font-medium text-gray-900">{status.last_revision ?? 'Never synced'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last Snapshot Revision</dt>
                <dd className="font-medium text-gray-900">{status.last_snapshot_revision ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last Success</dt>
                <dd className="font-medium text-gray-900">{status.last_success_at ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Currently Running Since</dt>
                <dd className="font-medium text-gray-900">{status.running_since ?? 'Not running'}</dd>
              </div>
              {status.last_error && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Last Error</dt>
                  <dd className="font-medium text-red-700">
                    {status.last_error} {status.last_error_at ? `(${status.last_error_at})` : ''}
                  </dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="text-gray-500">No status available.</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Local Mirror Row Counts</h2>

          {status ? (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Table</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Rows</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(status.table_counts).map(([table, count]) => (
                  <tr key={table}>
                    <td className="px-3 py-2 text-gray-900">{TABLE_LABELS[table] ?? table}</td>
                    <td className="px-3 py-2 text-right text-gray-900">{count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500">No data available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricebookSyncPage;
